import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import {
    IconClose,
    IconTooltip
} from '$partials/icons/index.js';

import { useKeyboard } from '$hooks/events.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const TooltipModal = ({
    description = null,
    heading = null
}) => {
    // references
    const dialogRef = useRef();

    // state
    const [ isSmallScreen, setIsSmallScreen ] = useState(true);
    const [ isOpen, setIsOpen ] = useState(false);

    // methods
    const handleClose = () => {
        setIsOpen(false);
    };

    const handleClick = (e) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    const handleResize = () => {
        handleClose();

        if (window.innerWidth < 768 && !isOpen) {
            setIsSmallScreen(true);
        }

        if (window.innerWidth >= 768 && isSmallScreen) {
            setIsSmallScreen(false);
        }
    };

    // hooks
    useKeyboard({
        'Escape': () => isOpen && handleClose()
    }, [ isOpen ]);

    useEffect(() => {
        window.addEventListener('resize', handleResize, { passive: true });

        handleResize();

        return () =>  window.removeEventListener('resize', handleResize);
    }, [ isSmallScreen ]);

    useEffect(() => {
        // use close/showModal methods to trigger browser's toplayer/inert mode
        const dialogNode = dialogRef.current;

        if (dialogNode) {
            if (isOpen) {
                if (dialogNode && !dialogNode.open) {
                    disableBodyScroll(dialogNode);
                    dialogNode.showModal?.();
                }
            } else {
                if (dialogNode && dialogNode.open) {
                    enableBodyScroll(dialogNode);
                    dialogNode.close?.();
                }
            }
        }

        return () => {
            if (dialogNode) {
                enableBodyScroll(dialogNode);
            }
        };
    }, [ isOpen ]);

    // render
    return (
        <div className='relative'>
            <button
                className='tooltip__button'
                onClick={isSmallScreen ? handleClick : null}
                onMouseEnter={isSmallScreen ? null : setIsOpen.bind(this, true)}
                onMouseLeave={isSmallScreen ? null : setIsOpen.bind(this, false)}
                type='button'>
                <span className='sr-only'>
                    Open Tooltip
                </span>

                <IconTooltip />
            </button>

            {(!isSmallScreen && isOpen) && (
                <div className='tooltip__fake-dialog absolute top-0 -left-[200px] bg-white rounded p-20 border border-charcoal w-[200px]'>
                    <div className='layout-item-padding'>
                        {heading && (
                            <p className='type-style-5 pb-20'>
                                {heading}
                            </p>
                        )}

                        {description && (
                            <p className='type-style-5'>
                                {description}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {isSmallScreen && (
                <dialog
                    onClick={handleClose}
                    ref={dialogRef}>
                    <div className='fixed inset-0'>
                        <div
                            className='absolute bottom-0 left-0 w-full bg-white rounded-tl rounded-tr p-20 border-l border-r border-t border-charcoal'
                            onClick={(e) => e.stopPropagation()}>
                            <div className='flex gap-20 justify-between'>
                                <div>
                                    <div className='layout-item-padding'>
                                        {heading && (
                                            <h1 className='type-style-5 pb-20'>
                                                {heading}
                                            </h1>
                                        )}

                                        {description && (
                                            <p className='type-style-5'>
                                                {description}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className='md:hidden flex-shrink-0'>
                                    <button
                                        className='ml-auto rounded-full'
                                        onClick={handleClose}
                                        type='button'>
                                        <span className='sr-only'>
                                            Close Tooltip
                                        </span>

                                        <IconClose />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </dialog>
            )}
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    description: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired
};

TooltipModal.propTypes = propTypes;

export default TooltipModal;
